<template>
  <div class="member-type-selector">
    <el-row :gutter="20">
      <!-- 个人会员 -->
      <el-col :xs="24" :sm="8">
        <div 
          class="type-card" 
          :class="{ active: modelValue === MEMBER_TYPES.INDIVIDUAL }"
          @click="selectType(MEMBER_TYPES.INDIVIDUAL)"
        >
          <div class="card-header">
            <el-icon :size="40"><User /></el-icon>
            <h3>个人会员</h3>
          </div>
          <div class="card-body">
            <div class="level-options" v-if="modelValue === MEMBER_TYPES.INDIVIDUAL" @click.stop>
              <el-radio-group v-model="selectedLevel" @change="handleLevelChange">
                <el-radio :label="MEMBER_LEVELS.NORMAL">
                  <div class="level-info">
                    <span class="level-name">普通会员</span>
                    <span class="level-price" v-if="feeConfig.normal">¥{{ feeConfig.normal }}/年</span>
                    <span class="level-price" v-else>加载中...</span>
                  </div>
                </el-radio>
                <el-radio :label="MEMBER_LEVELS.REGISTERED">
                  <div class="level-info">
                    <span class="level-name">注册会员</span>
                    <span class="level-price" v-if="feeConfig.registered">¥{{ feeConfig.registered }}/年</span>
                    <span class="level-price" v-else>加载中...</span>
                  </div>
                </el-radio>
              </el-radio-group>
            </div>
            <ul class="permissions-list">
              <li><el-icon><Check /></el-icon> 浏览课程信息</li>
              <li><el-icon><Check /></el-icon> 购买课程</li>
              <li v-if="selectedLevel === MEMBER_LEVELS.REGISTERED">
                <el-icon><Check /></el-icon> 个人信息展示
              </li>
            </ul>
          </div>
        </div>
      </el-col>

      <!-- 机构会员 -->
      <el-col :xs="24" :sm="8">
        <div 
          class="type-card" 
          :class="{ active: modelValue === MEMBER_TYPES.ORGANIZATION }"
          @click="selectType(MEMBER_TYPES.ORGANIZATION)"
        >
          <div class="card-header">
            <el-icon :size="40"><OfficeBuilding /></el-icon>
            <h3>机构会员</h3>
          </div>
          <div class="card-body">
            <div class="price-info">
              <span class="price">面议</span>
            </div>
            <ul class="permissions-list">
              <li><el-icon><Check /></el-icon> 机构信息展示</li>
              <li><el-icon><Check /></el-icon> 社会服务搜索</li>
              <li><el-icon><Check /></el-icon> 查看完整会员信息</li>
            </ul>
          </div>
        </div>
      </el-col>

      <!-- 理事会员 -->
      <el-col :xs="24" :sm="8">
        <div 
          class="type-card" 
          :class="{ active: modelValue === MEMBER_TYPES.DIRECTOR }"
          @click="selectType(MEMBER_TYPES.DIRECTOR)"
        >
          <div class="card-header">
            <el-icon :size="40"><Star /></el-icon>
            <h3>理事会员</h3>
          </div>
          <div class="card-body">
            <div class="price-info">
              <span class="price">面议</span>
            </div>
            <ul class="permissions-list">
              <li><el-icon><Check /></el-icon> 所有个人会员权限</li>
              <li><el-icon><Check /></el-icon> 发布新闻</li>
              <li><el-icon><Check /></el-icon> 管理新闻</li>
            </ul>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { User, OfficeBuilding, Star, Check } from '@element-plus/icons-vue'
import { MEMBER_TYPE as MEMBER_TYPES, MEMBER_LEVEL as MEMBER_LEVELS } from '@/utils/constants'
import api from '@/api'

export default {
  name: 'MemberTypeSelector',
  
  components: {
    User,
    OfficeBuilding,
    Star,
    Check
  },
  
  props: {
    modelValue: {
      type: Number,
      default: null
    },
    // 个人会员等级（仅当选择个人会员时有效）
    level: {
      type: Number,
      default: MEMBER_LEVELS.NORMAL
    }
  },
  
  emits: ['update:modelValue', 'update:level', 'change', 'permissionsChange'],
  
  data() {
    return {
      MEMBER_TYPES,
      MEMBER_LEVELS,
      selectedLevel: this.level || MEMBER_LEVELS.NORMAL,
      feeConfig: {
        normal: null,
        registered: null
      },
      loading: false
    }
  },
  
  computed: {
    /**
     * 根据当前选择的会员类型和等级，返回对应的权益列表
     */
    currentPermissions() {
      if (this.modelValue === MEMBER_TYPES.INDIVIDUAL) {
        const list = ['浏览课程信息', '购买课程']
        if (this.selectedLevel === MEMBER_LEVELS.REGISTERED) {
          list.push('个人信息展示')
        }
        return list
      }
      if (this.modelValue === MEMBER_TYPES.ORGANIZATION) {
        return ['机构信息展示', '社会服务搜索', '查看完整会员信息']
      }
      if (this.modelValue === MEMBER_TYPES.DIRECTOR) {
        return ['所有个人会员权限', '发布新闻', '管理新闻']
      }
      return []
    }
  },

  watch: {
    level(newVal) {
      this.selectedLevel = newVal
    },
    modelValue(newVal) {
      // 如果不是个人会员，重置等级选择
      if (newVal !== MEMBER_TYPES.INDIVIDUAL) {
        this.selectedLevel = MEMBER_LEVELS.NORMAL
      }
    },
    currentPermissions: {
      handler(val) {
        this.$emit('permissionsChange', val)
      },
      immediate: true
    }
  },
  
  created() {
    // 初始加载个人会员的费用配置
    this.loadFeeConfig(MEMBER_LEVELS.NORMAL)
    this.loadFeeConfig(MEMBER_LEVELS.REGISTERED)
  },
  
  methods: {
    /**
     * 加载会费配置
     */
    async loadFeeConfig(memberLevel) {
      try {
        this.loading = true
        const res = await api.getFeeConfig({
          member_type: MEMBER_TYPES.INDIVIDUAL,
          member_level: memberLevel
        })
        
        if (res.code === 2000 && res.data && res.data.specific_config) {
          const fee = res.data.specific_config.fee
          if (memberLevel === MEMBER_LEVELS.NORMAL) {
            this.feeConfig.normal = fee
          } else if (memberLevel === MEMBER_LEVELS.REGISTERED) {
            this.feeConfig.registered = fee
          }
        }
      } catch (error) {
        console.error('获取会费配置失败:', error)
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 选择会员类型
     */
    async selectType(type) {
      console.log('selectType called with:', type)
      console.log('Current modelValue:', this.modelValue)
      
      this.$emit('update:modelValue', type)
      
      // 等待 DOM 更新
      await this.$nextTick()
      
      // 如果是个人会员，确保有等级选择
      if (type === MEMBER_TYPES.INDIVIDUAL) {
        // 如果还没有选择等级，默认选择普通会员
        if (!this.selectedLevel) {
          this.selectedLevel = MEMBER_LEVELS.NORMAL
        }
        console.log('Selected level:', this.selectedLevel)
        this.$emit('update:level', this.selectedLevel)
        // 加载费用配置
        if (!this.feeConfig.normal) this.loadFeeConfig(MEMBER_LEVELS.NORMAL)
        if (!this.feeConfig.registered) this.loadFeeConfig(MEMBER_LEVELS.REGISTERED)
      } else {
        this.$emit('update:level', null)
      }
      
      this.$emit('change', {
        type,
        level: type === MEMBER_TYPES.INDIVIDUAL ? this.selectedLevel : null
      })
    },
    
    /**
     * 处理等级变化
     */
    handleLevelChange(level) {
      this.$emit('update:level', level)
      this.$emit('change', {
        type: this.modelValue,
        level
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.member-type-selector {
  .type-card {
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.3s;
    margin-bottom: 20px;
    
    &:hover {
      border-color: #409eff;
      box-shadow: 0 2px 12px rgba(64, 158, 255, 0.2);
    }
    
    &.active {
      border-color: #409eff;
      background-color: #ecf5ff;
    }
    
    .card-header {
      text-align: center;
      margin-bottom: 20px;
      
      .el-icon {
        color: #409eff;
        margin-bottom: 10px;
      }
      
      h3 {
        margin: 0;
        font-size: 20px;
        color: #303133;
      }
    }
    
    .card-body {
      .level-options {
        margin-bottom: 20px;
        padding: 15px;
        background-color: #fff;
        border-radius: 4px;
        
        .el-radio-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        
        .el-radio {
          margin-right: 0;
          width: 100%;
        }
        
        .level-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          
          .level-name {
            font-weight: 500;
          }
          
          .level-price {
            color: #f56c6c;
            font-weight: bold;
          }
        }
      }
      
      .price-info {
        text-align: center;
        margin-bottom: 20px;
        
        .price {
          font-size: 24px;
          color: #f56c6c;
          font-weight: bold;
        }
      }
      
      .permissions-list {
        list-style: none;
        padding: 0;
        margin: 0;
        
        li {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 0;
          color: #606266;
          
          .el-icon {
            color: #67c23a;
            font-size: 16px;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .member-type-selector {
    .type-card {
      margin-bottom: 15px;
    }
  }
}
</style>
